import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image as ExpoImage } from 'expo-image';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import { getLoggedInStudentId, hasSuperUserAccess } from '../utils/sessionUser';
import { fetchVisibleStudents } from '../utils/fetchVisibleStudents';
import {
  exercisesListUrl,
  exerciseTypesByCourseUrl,
  exerciseTypesByGradeUrl,
  nonCourseExerciseTypesUrl,
} from '../utils/exerciseApi';
import {
  exerciseCountsAsAssignedInAssignmentsUi,
  readExerciseMyOnlyFlag,
} from '../utils/exerciseMyOnly';

const { width } = Dimensions.get('window');
const COURSES_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');
const PLACEHOLDER_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');

const TABS = [
  { id: 'assign', label: 'Assign Exercise' },
  { id: 'my-assigned', label: 'My Assigned Exercises' },
];

const ASSIGNMENT_TABS = [
  { id: 'course', label: 'Course' },
  { id: 'grade', label: 'Grade' },
  { id: 'exercise-only', label: 'Exercise Only' },
];

export default function AssignmentsPage({ navigation, route }) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('assign');
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const stopwatchRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsFetchError, setStudentsFetchError] = useState('');

  // Nested tabs state
  const [activeAssignmentTab, setActiveAssignmentTab] = useState('course');
  
  // Course tab state
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [selectedExerciseType, setSelectedExerciseType] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState(new Set());
  const [selectAllExercises, setSelectAllExercises] = useState(false);
  const [courseExercises, setCourseExercises] = useState([]);
  const [selectAllCourseExercises, setSelectAllCourseExercises] = useState(false);
  const [loadingCourseExercises, setLoadingCourseExercises] = useState(false);
  const [alreadyAssignedExercises, setAlreadyAssignedExercises] = useState(new Set());
  const [showCoursesDropdown, setShowCoursesDropdown] = useState(false);
  const [showExerciseTypesDropdown, setShowExerciseTypesDropdown] = useState(false);
  const [showExercisesList, setShowExercisesList] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingExerciseTypes, setLoadingExerciseTypes] = useState(false);
  const [loadingExercises, setLoadingExercises] = useState(false);

  // Grade tab state
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showGradesDropdown, setShowGradesDropdown] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [gradeExercises, setGradeExercises] = useState([]);
  const [selectAllGradeExercises, setSelectAllGradeExercises] = useState(false);
  const [loadingGradeExercises, setLoadingGradeExercises] = useState(false);

  // Exercise Only tab state
  const [exerciseOnlyExercises, setExerciseOnlyExercises] = useState([]);
  const [selectAllExerciseOnlyExercises, setSelectAllExerciseOnlyExercises] = useState(false);
  const [loadingExerciseOnlyExercises, setLoadingExerciseOnlyExercises] = useState(false);

  const [selectedExerciseForDetail, setSelectedExerciseForDetail] = useState(null);
  const [showExerciseDetailModal, setShowExerciseDetailModal] = useState(false);
  const [assignmentSuccess, setAssignmentSuccess] = useState(null);
  const [assignmentIdsByExerciseId, setAssignmentIdsByExerciseId] = useState({});

  useEffect(() => {
    const loadUser = async () => {
      let user = route?.params?.user;
      const uid =
        route?.params?.userId ?? user?.id ?? (await getLoggedInStudentId());
      if (uid && !user) {
        try {
          const res = await fetch(`${getApiUrl()}/api/users/${uid}`);
          if (res.ok) user = await res.json();
        } catch {
          /* ignore */
        }
      }
      if (user) setCurrentUser(user);
    };
    void loadUser();
  }, [route?.params?.user, route?.params?.userId]);

  const loadStudentsForPicker = useCallback(async () => {
    setLoadingStudents(true);
    setStudentsFetchError('');
    const { students: rows, error } = await fetchVisibleStudents({ requester: currentUser });
    setStudents(rows);
    if (error) setStudentsFetchError(error);
    setLoadingStudents(false);
  }, [currentUser?.id]);

  useFocusEffect(
    useCallback(() => {
      if (activeTab === 'assign') {
        void loadStudentsForPicker();
        void fetchCourses();
        void fetchGrades();
      }
    }, [activeTab, loadStudentsForPicker])
  );

  useEffect(() => {
    if (activeTab === 'assign' && activeAssignmentTab === 'course' && selectedCourse) {
      fetchExerciseTypesForCourse(selectedCourse.id);
    }
  }, [activeTab, activeAssignmentTab, selectedCourse, currentUser?.id]);

  useEffect(() => {
    if (activeTab === 'assign' && activeAssignmentTab === 'grade' && selectedGrade) {
      fetchExerciseTypesForGrade(selectedGrade.id);
    }
  }, [activeTab, activeAssignmentTab, selectedGrade, currentUser?.id]);

  useEffect(() => {
    if (activeTab === 'assign' && activeAssignmentTab === 'exercise-only' && currentUser?.id) {
      fetchExerciseTypesForExerciseOnly();
    }
  }, [activeTab, activeAssignmentTab, currentUser?.id]);

  useEffect(() => {
    if (
      activeTab === 'assign' &&
      activeAssignmentTab === 'course' &&
      selectedCourse &&
      selectedStudent &&
      currentUser?.id
    ) {
      fetchAllCourseExercises();
    } else if (activeAssignmentTab !== 'course') {
      setCourseExercises([]);
      setSelectAllCourseExercises(false);
    }
  }, [activeTab, activeAssignmentTab, selectedCourse, selectedStudent, currentUser?.id]);

  useEffect(() => {
    if (
      activeTab === 'assign' &&
      activeAssignmentTab === 'grade' &&
      selectedGrade &&
      selectedStudent &&
      currentUser?.id
    ) {
      fetchAllGradeExercises();
    } else if (activeAssignmentTab !== 'grade') {
      setGradeExercises([]);
      setSelectAllGradeExercises(false);
    }
  }, [activeTab, activeAssignmentTab, selectedGrade, selectedStudent, currentUser?.id]);

  useEffect(() => {
    if (
      activeTab === 'assign' &&
      activeAssignmentTab === 'exercise-only' &&
      selectedStudent &&
      currentUser?.id
    ) {
      fetchAllExerciseOnlyExercises();
    } else if (activeAssignmentTab !== 'exercise-only') {
      setExerciseOnlyExercises([]);
      setSelectAllExerciseOnlyExercises(false);
    }
  }, [activeTab, activeAssignmentTab, selectedStudent, currentUser?.id]);

  useEffect(() => {
    if (activeTab !== 'assign' || !selectedExerciseType) return;
    if (activeAssignmentTab === 'course' && selectedCourse) {
      fetchExercisesForType(selectedExerciseType.id);
    } else if (activeAssignmentTab === 'grade' && selectedGrade) {
      fetchExercisesForType(selectedExerciseType.id);
    } else if (activeAssignmentTab === 'exercise-only') {
      fetchExercisesForType(selectedExerciseType.id);
    }
  }, [
    activeTab,
    activeAssignmentTab,
    selectedExerciseType,
    selectedStudent,
    selectedCourse?.id,
    selectedGrade?.id,
  ]);

  const handleStudentDropdownPress = () => {
    const next = !showStudentDropdown;
    setShowStudentDropdown(next);
    if (next && students.length === 0 && !loadingStudents) {
      void loadStudentsForPicker();
    }
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudent(studentId);
    setShowStudentDropdown(false);
    setExercises([]);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
    setAlreadyAssignedExercises(new Set());
    setShowExercisesList(false);
    setCourseExercises([]);
    setSelectAllCourseExercises(false);
    setGradeExercises([]);
    setSelectAllGradeExercises(false);
    setExerciseOnlyExercises([]);
    setSelectAllExerciseOnlyExercises(false);
    setSelectedGrade(null);
    setShowGradesDropdown(false);
    setAssignmentIdsByExerciseId({});
  };

  const resetExerciseSelectionState = () => {
    setExercises([]);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
    setAlreadyAssignedExercises(new Set());
    setShowExercisesList(false);
    setSelectedExerciseType(null);
    setShowExerciseTypesDropdown(false);
    setAssignmentIdsByExerciseId({});
  };

  const handleAssignmentTabChange = (tabId) => {
    setActiveAssignmentTab(tabId);
    resetExerciseSelectionState();
    setSelectAllCourseExercises(false);
    setSelectAllGradeExercises(false);
    setSelectAllExerciseOnlyExercises(false);
    setAssignmentSuccess(null);
  };

  const isGradeBeyondViewerAccess = (grade) => {
    if (!grade || !currentUser) return false;
    if (hasSuperUserAccess(currentUser)) return false;
    const viewerRank =
      currentUser.student_grade_rank != null
        ? Number(currentUser.student_grade_rank)
        : null;
    const gradeRank =
      grade.grade_rank_order != null ? Number(grade.grade_rank_order) : null;
    if (viewerRank == null || gradeRank == null) return false;
    return gradeRank > viewerRank + 1;
  };

  const handleCreateExercisePress = () => {
    navigation.navigate('ExerciseBuilder', { 
      userId: currentUser?.id || 1, 
      user: currentUser 
    });
  };

  const getSelectedStudentName = () => {
    if (!selectedStudent) {
      return 'Select student...';
    }
    const student = students.find(s => s.id === selectedStudent);
    return student?.student_name || student?.username || 'Selected student';
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setGrades(
          rows.sort(
            (a, b) =>
              (a.grade_rank_order ?? 0) - (b.grade_rank_order ?? 0) ||
              String(a.grade_name).localeCompare(String(b.grade_name))
          )
        );
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchExerciseTypesForCourse = async (courseId) => {
    if (!currentUser?.id) return;
    setLoadingExerciseTypes(true);
    try {
      const url = exerciseTypesByCourseUrl(courseId, {
        viewerId: currentUser.id,
        forAssignment: true,
      });
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(data);
      }
    } catch (error) {
      console.error('Error fetching exercise types:', error);
    } finally {
      setLoadingExerciseTypes(false);
    }
  };

  const fetchExerciseTypesForGrade = async (gradeId) => {
    if (!currentUser?.id) return;
    setLoadingExerciseTypes(true);
    try {
      const url = exerciseTypesByGradeUrl(gradeId, {
        viewerId: currentUser.id,
        forAssignment: true,
      });
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(data);
      }
    } catch (error) {
      console.error('Error fetching grade exercise types:', error);
    } finally {
      setLoadingExerciseTypes(false);
    }
  };

  const fetchExerciseTypesForExerciseOnly = async () => {
    if (!currentUser?.id) return;
    setLoadingExerciseTypes(true);
    try {
      const url = nonCourseExerciseTypesUrl({
        viewerId: currentUser.id,
        forAssignment: true,
      });
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching exercise-only types:', error);
    } finally {
      setLoadingExerciseTypes(false);
    }
  };

  const fetchAllGradeExercises = async () => {
    if (!selectedGrade || !selectedStudent || !currentUser?.id) return;
    setLoadingGradeExercises(true);
    try {
      const url = exercisesListUrl({
        gradeId: selectedGrade.id,
        studentId: selectedStudent,
        viewerId: currentUser.id,
        forAssignment: true,
      });
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setGradeExercises(rows);
        if (rows.length > 0) {
          setAlreadyAssignedExercises((prev) => {
            const assignedSet = new Set(prev);
            rows.forEach((exercise) => {
              if (exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess)) {
                assignedSet.add(exercise.id);
              }
            });
            return assignedSet;
          });
          setAssignmentIdsByExerciseId((prev) => {
            const idMap = { ...prev };
            rows.forEach((exercise) => {
              if (
                exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess) &&
                exercise.assignment_id != null
              ) {
                idMap[exercise.id] = exercise.assignment_id;
              }
            });
            return idMap;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching grade exercises:', error);
    } finally {
      setLoadingGradeExercises(false);
    }
  };

  const refreshAssignmentExerciseData = async () => {
    if (activeAssignmentTab === 'grade') {
      await fetchAllGradeExercises();
    } else if (activeAssignmentTab === 'course') {
      await fetchAllCourseExercises();
    } else if (activeAssignmentTab === 'exercise-only') {
      await fetchAllExerciseOnlyExercises();
    }
    if (selectedExerciseType) {
      await fetchExercisesForType(selectedExerciseType.id);
    }
  };

  const fetchAllExerciseOnlyExercises = async () => {
    if (!selectedStudent || !currentUser?.id) return;
    setLoadingExerciseOnlyExercises(true);
    try {
      const url = exercisesListUrl({
        nonCourseOnly: true,
        studentId: selectedStudent,
        viewerId: currentUser.id,
        forAssignment: true,
      });
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setExerciseOnlyExercises(rows);
        if (rows.length > 0) {
          setAlreadyAssignedExercises((prev) => {
            const assignedSet = new Set(prev);
            rows.forEach((exercise) => {
              if (exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess)) {
                assignedSet.add(exercise.id);
              }
            });
            return assignedSet;
          });
          setAssignmentIdsByExerciseId((prev) => {
            const idMap = { ...prev };
            rows.forEach((exercise) => {
              if (
                exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess) &&
                exercise.assignment_id != null
              ) {
                idMap[exercise.id] = exercise.assignment_id;
              }
            });
            return idMap;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching exercise-only exercises:', error);
    } finally {
      setLoadingExerciseOnlyExercises(false);
    }
  };

  const fetchAllCourseExercises = async () => {
    if (!selectedCourse || !selectedStudent || !currentUser?.id) return;
    setLoadingCourseExercises(true);
    try {
      const url = exercisesListUrl({
        courseId: selectedCourse.id,
        studentId: selectedStudent,
        viewerId: currentUser.id,
        forAssignment: true,
      });
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        setCourseExercises(rows);
        if (rows.length > 0) {
          setAlreadyAssignedExercises((prev) => {
            const assignedSet = new Set(prev);
            rows.forEach((exercise) => {
              if (exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess)) {
                assignedSet.add(exercise.id);
              }
            });
            return assignedSet;
          });
          setAssignmentIdsByExerciseId((prev) => {
            const idMap = { ...prev };
            rows.forEach((exercise) => {
              if (
                exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess) &&
                exercise.assignment_id != null
              ) {
                idMap[exercise.id] = exercise.assignment_id;
              }
            });
            return idMap;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching course exercises:', error);
    } finally {
      setLoadingCourseExercises(false);
    }
  };

  const fetchExercisesForType = async (exerciseTypeId) => {
    if (!currentUser?.id) return;
    setLoadingExercises(true);
    try {
      const url = exercisesListUrl({
        exerciseTypeId,
        courseId: activeAssignmentTab === 'course' ? selectedCourse?.id : undefined,
        gradeId: activeAssignmentTab === 'grade' ? selectedGrade?.id : undefined,
        nonCourseOnly: activeAssignmentTab === 'exercise-only' ? true : undefined,
        studentId: selectedStudent,
        viewerId: currentUser.id,
        forAssignment: true,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setExercises(data);

        const hasScope =
          selectedStudent &&
          ((activeAssignmentTab === 'course' && selectedCourse) ||
            (activeAssignmentTab === 'grade' && selectedGrade) ||
            activeAssignmentTab === 'exercise-only');

        if (hasScope && data.length > 0) {
          const typeIds = new Set(data.map((exercise) => exercise.id));
          setAlreadyAssignedExercises((prev) => {
            const assignedSet = new Set(prev);
            typeIds.forEach((id) => assignedSet.delete(id));
            data.forEach((exercise) => {
              if (exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess)) {
                assignedSet.add(exercise.id);
              }
            });
            return assignedSet;
          });
          setAssignmentIdsByExerciseId((prev) => {
            const idMap = { ...prev };
            typeIds.forEach((id) => {
              delete idMap[id];
            });
            data.forEach((exercise) => {
              if (
                exerciseCountsAsAssignedInAssignmentsUi(exercise, currentUser, hasSuperUserAccess) &&
                exercise.assignment_id != null
              ) {
                idMap[exercise.id] = exercise.assignment_id;
              }
            });
            return idMap;
          });
        } else if (!hasScope) {
          setAlreadyAssignedExercises(new Set());
          setAssignmentIdsByExerciseId({});
        }

        setShowExercisesList(true);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoadingExercises(false);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setShowCoursesDropdown(false);
    resetExerciseSelectionState();
  };

  const handleGradeSelect = (grade) => {
    setSelectedGrade(grade);
    setShowGradesDropdown(false);
    resetExerciseSelectionState();
  };

  const handleExerciseTypeSelect = (exerciseType) => {
    setSelectedExerciseType(exerciseType);
    setShowExerciseTypesDropdown(false);
  };

  const handleExerciseToggle = (exerciseId, e) => {
    if (e) {
      e.stopPropagation();
    }
    const newSelected = new Set(selectedExercises);
    if (newSelected.has(exerciseId)) {
      newSelected.delete(exerciseId);
    } else {
      newSelected.add(exerciseId);
    }
    setSelectedExercises(newSelected);
    syncSelectAllFlags(newSelected);
  };

  const handleExerciseDetailClick = (exercise) => {
    setSelectedExerciseForDetail(exercise);
    setShowExerciseDetailModal(true);
  };

  const isYouTubeUrl = (url) => {
    if (!url) return false;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11
      ? `https://www.youtube.com/embed/${match[2]}`
      : null;
  };

  const syncSelectAllFlags = (nextSelected) => {
    const typeIds = exercises.map((e) => e.id);
    setSelectAllExercises(
      typeIds.length > 0 && typeIds.every((id) => nextSelected.has(id))
    );
    const courseIds = courseExercises.map((e) => e.id);
    setSelectAllCourseExercises(
      courseIds.length > 0 && courseIds.every((id) => nextSelected.has(id))
    );
    const gradeIds = gradeExercises.map((e) => e.id);
    setSelectAllGradeExercises(
      gradeIds.length > 0 && gradeIds.every((id) => nextSelected.has(id))
    );
    const exerciseOnlyIds = exerciseOnlyExercises.map((e) => e.id);
    setSelectAllExerciseOnlyExercises(
      exerciseOnlyIds.length > 0 && exerciseOnlyIds.every((id) => nextSelected.has(id))
    );
  };

  const handleSelectAllToggle = () => {
    if (selectAllExercises) {
      const typeIdSet = new Set(exercises.map((e) => e.id));
      const nextSelected = new Set(
        [...selectedExercises].filter((id) => !typeIdSet.has(id))
      );
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    } else {
      const nextSelected = new Set(selectedExercises);
      exercises.forEach((e) => nextSelected.add(e.id));
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    }
  };

  const handleSelectAllCourseToggle = () => {
    if (selectAllCourseExercises) {
      const courseIdSet = new Set(courseExercises.map((e) => e.id));
      const nextSelected = new Set(
        [...selectedExercises].filter((id) => !courseIdSet.has(id))
      );
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    } else {
      const nextSelected = new Set(selectedExercises);
      courseExercises.forEach((e) => nextSelected.add(e.id));
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    }
  };

  const handleSelectAllGradeToggle = () => {
    if (selectAllGradeExercises) {
      const gradeIdSet = new Set(gradeExercises.map((e) => e.id));
      const nextSelected = new Set(
        [...selectedExercises].filter((id) => !gradeIdSet.has(id))
      );
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    } else {
      const nextSelected = new Set(selectedExercises);
      gradeExercises.forEach((e) => nextSelected.add(e.id));
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    }
  };

  const handleSelectAllExerciseOnlyToggle = () => {
    if (selectAllExerciseOnlyExercises) {
      const scopeIdSet = new Set(exerciseOnlyExercises.map((e) => e.id));
      const nextSelected = new Set(
        [...selectedExercises].filter((id) => !scopeIdSet.has(id))
      );
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    } else {
      const nextSelected = new Set(selectedExercises);
      exerciseOnlyExercises.forEach((e) => nextSelected.add(e.id));
      setSelectedExercises(nextSelected);
      syncSelectAllFlags(nextSelected);
    }
  };

  const handleAssign = async () => {
    if (!selectedStudent) {
      alert('Please select a student');
      return;
    }
    if (selectedExercises.size === 0) {
      alert('Please select at least one exercise');
      return;
    }
    
    if (!currentUser?.id) {
      alert('User not logged in');
      return;
    }
    
    try {
      // Filter out exercises that are already assigned (active = true)
      const exercisesToAssign = Array.from(selectedExercises).filter(
        exerciseId => !alreadyAssignedExercises.has(exerciseId)
      );
      
      if (exercisesToAssign.length === 0) {
        alert('All selected exercises are already assigned to this student');
        return;
      }
      
      // Get selected student name for success message
      const selectedStudentObj = students.find(s => s.id === selectedStudent);
      const studentName = selectedStudentObj?.student_name || selectedStudentObj?.username || 'Student';
      
      // Create assignments in one batch (groups notifications by exercise type)
      const batchResponse = await fetch(`${getApiUrl()}/api/student-exercise-assignments/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: selectedStudent,
          exercise_ids: exercisesToAssign,
          assigned_by: currentUser.id,
          active: true,
          notes: `assignment by ${currentUser.student_name || currentUser.username} in app.`,
        }),
      });

      if (!batchResponse.ok) {
        alert('Failed to assign exercises. Please try again.');
        return;
      }
      
      // Show success message
      setAssignmentSuccess({
        message: `Exercises have been assigned to ${studentName}`,
        count: exercisesToAssign.length,
      });
      
      // Clear selected exercises and refresh assignment status
      setSelectedExercises(new Set());
      setSelectAllExercises(false);
      setSelectAllCourseExercises(false);
      setSelectAllGradeExercises(false);
      setSelectAllExerciseOnlyExercises(false);

      await refreshAssignmentExerciseData();
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setAssignmentSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error assigning exercises:', error);
      alert('An error occurred while assigning exercises. Please try again.');
    }
  };

  const handleRetire = async () => {
    if (!selectedStudent) {
      alert('Please select a student');
      return;
    }

    const toRetire = Array.from(selectedExercises).filter(
      (exerciseId) =>
        alreadyAssignedExercises.has(exerciseId) &&
        assignmentIdsByExerciseId[exerciseId]
    );

    if (toRetire.length === 0) {
      alert('Select at least one actively assigned exercise to retire');
      return;
    }

    try {
      const results = await Promise.all(
        toRetire.map((exerciseId) =>
          fetch(
            `${getApiUrl()}/api/student-exercise-assignments/${assignmentIdsByExerciseId[exerciseId]}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ active: false }),
            }
          )
        )
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        alert(`Failed to retire ${failed.length} exercise(s). Please try again.`);
        return;
      }

      const studentName =
        students.find((s) => s.id === selectedStudent)?.student_name ||
        students.find((s) => s.id === selectedStudent)?.username ||
        'Student';
      setAssignmentSuccess({
        message: `Retired ${toRetire.length} exercise(s) for ${studentName}`,
        count: toRetire.length,
      });
      setSelectedExercises(new Set());
      setSelectAllExercises(false);
      await refreshAssignmentExerciseData();
      setTimeout(() => setAssignmentSuccess(null), 3000);
    } catch (error) {
      console.error('Error retiring exercises:', error);
      alert('An error occurred while retiring exercises. Please try again.');
    }
  };

  const selectedExerciseIds = Array.from(selectedExercises);
  const selectedAssignedCount = selectedExerciseIds.filter((exerciseId) =>
    alreadyAssignedExercises.has(exerciseId)
  ).length;
  const selectedUnassignedCount = selectedExerciseIds.length - selectedAssignedCount;
  const selectedRetirableCount = selectedExerciseIds.filter(
    (exerciseId) =>
      alreadyAssignedExercises.has(exerciseId) && assignmentIdsByExerciseId[exerciseId]
  ).length;
  const canAssign = Boolean(selectedStudent) && selectedUnassignedCount > 0;
  const canRetire = Boolean(selectedStudent) && selectedRetirableCount > 0;

  const renderExerciseAssignmentList = () => {
    if (!showExercisesList || exercises.length === 0) return null;

    return (
      <View style={styles.exercisesDropdown} data-assignment-exercise-list="true">
        <View style={styles.assignmentTableHeader} data-assignment-table-header="true">
          <View style={styles.assignmentTableSelectCol}>
            <Text style={styles.assignmentTableHeaderLabel}>Select all</Text>
            <TouchableOpacity
              style={styles.assignmentTableHeaderCheckbox}
              onPress={handleSelectAllToggle}
            >
              <View
                style={[
                  styles.exerciseCheckbox,
                  selectAllExercises && styles.exerciseCheckboxSelected,
                ]}
              >
                {selectAllExercises && <Text style={styles.checkmark}>✓</Text>}
              </View>
            </TouchableOpacity>
          </View>
          <View style={styles.assignmentTableExerciseCol} />
          <View style={styles.assignmentTableAlreadyAssignedCol}>
            <View style={styles.assignmentTableAlreadyAssignedHeader}>
              <Text style={styles.assignmentTableHeaderLabelGreen}>Already</Text>
              <Text style={styles.assignmentTableHeaderLabelGreen}>Assigned</Text>
              <View style={styles.assignmentTableGreenUnderline} />
            </View>
          </View>
        </View>

        {exercises.map((exercise) => {
          const isSelected = selectedExercises.has(exercise.id);
          const hideAssignedUi =
            hasSuperUserAccess(currentUser) && readExerciseMyOnlyFlag(exercise);
          const isAlreadyAssigned =
            !hideAssignedUi && alreadyAssignedExercises.has(exercise.id);
          return (
            <View
              key={exercise.id}
              style={styles.exerciseRowWrapper}
              data-assignment-exercise-row="true"
            >
              <View style={styles.assignmentTableSelectCol}>
                <TouchableOpacity
                  style={styles.assignmentTableHeaderCheckbox}
                  onPress={() => handleExerciseToggle(exercise.id)}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.exerciseCheckbox,
                      isSelected && styles.exerciseCheckboxSelected,
                    ]}
                  >
                    {isSelected && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                </TouchableOpacity>
              </View>
              <TouchableOpacity
                style={[styles.exerciseTile, isSelected && styles.exerciseTileSelected]}
                onPress={() => handleExerciseDetailClick(exercise)}
                activeOpacity={0.7}
              >
                {isSelected && <View style={styles.yellowOverlay} />}
                <View style={styles.assignmentTableRow}>
                  <View
                    style={styles.assignmentTableExerciseCol}
                    data-assignment-exercise-info="true"
                  >
                    <View style={styles.exerciseInfo}>
                      <Text
                        style={[
                          styles.exerciseName,
                          isSelected && styles.exerciseNameSelected,
                        ]}
                        numberOfLines={2}
                      >
                        {exercise.exercise_name}
                      </Text>
                      <Text
                        style={[
                          styles.exerciseDetails,
                          isSelected && styles.exerciseDetailsSelected,
                        ]}
                        numberOfLines={1}
                      >
                        {exercise.sets} sets × {exercise.reps} reps
                      </Text>
                    </View>
                  </View>
                  <View style={styles.assignmentTableAlreadyAssignedCol}>
                    {!hideAssignedUi ? (
                      <View
                        style={[
                          styles.exerciseCheckbox,
                          styles.checkboxGreen,
                          isAlreadyAssigned && styles.checkboxSelectedGreen,
                        ]}
                      >
                        {isAlreadyAssigned && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                    ) : null}
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          );
        })}

        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[
              styles.actionButton,
              styles.assignButton,
              !canAssign && styles.assignButtonDisabled,
            ]}
            nativeID="assignments-assign-btn"
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    assignmentsAssignBtn: 'true',
                    ...(!canAssign ? { assignmentsAssignBtnDisabled: 'true' } : {}),
                  },
                }
              : {})}
            onPress={handleAssign}
            disabled={!canAssign}
          >
            <Text style={styles.actionButtonText}>
              Assign
              {selectedUnassignedCount > 0 ? ` (${selectedUnassignedCount})` : ''}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.actionButton,
              styles.retireButton,
              !canRetire && styles.retireButtonDisabled,
            ]}
            nativeID="assignments-retire-btn"
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    assignmentsRetireBtn: 'true',
                    ...(!canRetire ? { assignmentsRetireBtnDisabled: 'true' } : {}),
                  },
                }
              : {})}
            onPress={handleRetire}
            disabled={!canRetire}
          >
            <Text
              style={[
                styles.actionButtonText,
                !canRetire && styles.retireButtonTextDisabled,
              ]}
            >
              Retire
              {selectedRetirableCount > 0 ? ` (${selectedRetirableCount})` : ''}
            </Text>
          </TouchableOpacity>
        </View>

        {assignmentSuccess && (
          <View style={styles.assignmentSuccessBox}>
            <Text style={styles.assignmentSuccessText}>
              {assignmentSuccess.message}
              {assignmentSuccess.count > 1 && ` (${assignmentSuccess.count} exercises)`}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const styles = getStyles(theme);

  return (
    <View
      style={styles.container}
      data-screen="assignments"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer} data-light-mode={!theme.isDarkMode} data-header-element="hamburger">
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer} data-light-mode={!theme.isDarkMode} data-header-element="breadcrumb">
          <Text style={styles.breadcrumb}>Assignments Page</Text>
        </View>
        <View style={styles.profileIconContainer} data-light-mode={!theme.isDarkMode} data-header-element="profile">
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      {/* Tabs Container - 90% width, centered */}
      <View style={styles.tabsWrapper}>
        <View style={styles.tabsContainer}>
          {TABS.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                activeTab === tab.id && styles.tabActive,
              ]}
              onPress={() => setActiveTab(tab.id)}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.id && styles.tabTextActive,
                ]}
              >
                {tab.label}
              </Text>
              {activeTab === tab.id && (
                <View style={styles.tabIndicator} />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        {activeTab === 'assign' && (
          <View style={styles.tabContent}>
            {/* Create Exercise Link */}
            <View style={styles.createExerciseLink}>
              <Text style={styles.createExerciseText}>
                Exercise not exist here? Create{' '}
              </Text>
              <TouchableOpacity
                onPress={handleCreateExercisePress}
                style={styles.createExerciseButton}
              >
                <Text style={styles.createExerciseButtonText}>here</Text>
              </TouchableOpacity>
            </View>

            {/* Student dropdown — in-flow list (matches Assignments Builder / Send Message) */}
            <View style={styles.formField}>
              <Text style={styles.label}>Select Student</Text>
              <Text style={styles.helpText}>Visible students on the club roster</Text>
              <TouchableOpacity
                style={styles.dropdownTrigger}
                nativeID="assignments-student-dropdown-trigger"
                {...(Platform.OS === 'web'
                  ? { dataSet: { assignmentsStudentDropdownTrigger: 'true', dropdownTrigger: 'true' } }
                  : {})}
                onPress={handleStudentDropdownPress}
                disabled={loadingStudents}
              >
                {loadingStudents ? (
                  <ActivityIndicator size="small" color={theme.colors.heading} />
                ) : (
                  <>
                    <Text
                      style={[
                        styles.dropdownTriggerText,
                        !selectedStudent && styles.dropdownButtonTextPlaceholder,
                      ]}
                      numberOfLines={1}
                    >
                      {getSelectedStudentName()}
                    </Text>
                    <Text style={styles.dropdownArrow}>
                      {showStudentDropdown ? '▲' : '▼'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>

              {showStudentDropdown && students.length > 0 ? (
                <ScrollView
                  style={styles.dropdownList}
                  nativeID="assignments-student-dropdown-list"
                  {...(Platform.OS === 'web'
                    ? { dataSet: { assignmentsStudentDropdownList: 'true', dropdownSurface: 'true' } }
                    : {})}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                >
                  {students.map((student) => {
                    const isSelected = selectedStudent === student.id;
                    return (
                      <TouchableOpacity
                        key={student.id}
                        style={[
                          styles.dropdownListItem,
                          isSelected && styles.dropdownOptionSelected,
                        ]}
                        onPress={() => handleStudentSelect(student.id)}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownRow: 'true' } }
                          : {})}
                      >
                        <Text
                          style={[
                            styles.dropdownListItemText,
                            isSelected && styles.dropdownOptionTextSelected,
                          ]}
                        >
                          {student.student_name || student.username}
                          {student.student_grade ? ` · ${student.student_grade}` : ''}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : null}
              {showStudentDropdown && !loadingStudents && students.length === 0 ? (
                <Text style={styles.emptyText}>
                  {studentsFetchError || 'No visible students found.'}
                </Text>
              ) : null}
            </View>

            {/* Nested Tabs Section — single card grows with in-flow dropdowns */}
            <View style={styles.nestedTabsWrapper}>
              <View
                style={styles.nestedTabsCard}
                {...(Platform.OS === 'web'
                  ? { dataSet: { assignmentsNestedCard: 'true' } }
                  : {})}
              >
              <View style={styles.nestedTabsContainer}>
                {ASSIGNMENT_TABS.map((tab) => (
                  <TouchableOpacity
                    key={tab.id}
                    style={[
                      styles.nestedTab,
                      activeAssignmentTab === tab.id && styles.nestedTabActive,
                    ]}
                    onPress={() => handleAssignmentTabChange(tab.id)}
                    activeOpacity={0.7}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { assignmentsNestedTab: 'true' } }
                      : {})}
                  >
                    <Text
                      style={[
                        styles.nestedTabText,
                        activeAssignmentTab === tab.id && styles.nestedTabTextActive,
                      ]}
                    >
                      {tab.label}
                    </Text>
                    {activeAssignmentTab === tab.id && (
                      <View style={styles.nestedTabIndicator} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>

              {/* Nested Tab Content */}
              {activeAssignmentTab === 'course' && (
                <View style={styles.nestedTabContent}>
                  {/* Course Tile */}
                  <View style={styles.courseTileContainer}>
                    <TouchableOpacity
                      style={styles.courseTile}
                      onPress={() => setShowCoursesDropdown(!showCoursesDropdown)}
                      activeOpacity={0.8}
                    >
                      <ExpoImage
                        source={COURSES_IMAGE}
                        style={styles.courseTileImage}
                        contentFit="contain"
                      />
                      <TouchableOpacity
                        style={styles.courseTileButton}
                        onPress={() => setShowCoursesDropdown(!showCoursesDropdown)}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownTrigger: 'true' } }
                          : {})}
                      >
                        <Text style={styles.courseTileButtonText}>
                          {selectedCourse ? selectedCourse.course_name : 'Select Course'}
                        </Text>
                      </TouchableOpacity>
                    </TouchableOpacity>

                    {/* Courses Dropdown */}
                    {showCoursesDropdown && (
                      <View
                        style={styles.coursesDropdown}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownSurface: 'true' } }
                          : {})}
                      >
                        {loadingCourses ? (
                          <Text style={styles.loadingText}>Loading courses...</Text>
                        ) : courses.length === 0 ? (
                          <Text style={styles.emptyText}>No courses found</Text>
                        ) : (
                          courses.map((course) => (
                            <TouchableOpacity
                              key={course.id}
                              style={styles.coursesDropdownItem}
                              onPress={() => handleCourseSelect(course)}
                              {...(Platform.OS === 'web'
                                ? { dataSet: { dropdownRow: 'true' } }
                                : {})}
                            >
                              <Text style={styles.coursesDropdownItemText}>
                                {course.course_name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </View>
                    )}
                  </View>

                  {/* Select all exercises in course */}
                  {selectedCourse &&
                    selectedStudent &&
                    courseExercises.length > 0 && (
                    <View style={styles.selectAllCourseContainer}>
                      <View style={styles.assignmentTableSelectCol}>
                        <TouchableOpacity
                          style={styles.assignmentTableHeaderCheckbox}
                          onPress={handleSelectAllCourseToggle}
                          disabled={loadingCourseExercises}
                        >
                          <View
                            style={[
                              styles.exerciseCheckbox,
                              selectAllCourseExercises && styles.exerciseCheckboxSelected,
                            ]}
                          >
                            {selectAllCourseExercises && (
                              <Text style={styles.checkmark}>✓</Text>
                            )}
                          </View>
                        </TouchableOpacity>
                      </View>
                      <Text
                        style={[styles.selectAllCourseLabel, styles.selectAllCourseLabelFlex]}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { assignmentsSelectAllLabel: 'true' } }
                          : {})}
                      >
                        Assign all exercises in this course for this student
                      </Text>
                      <View style={styles.assignmentTableAlreadyAssignedCol} />
                    </View>
                  )}

                  {/* Exercise Types Dropdown */}
                  {selectedCourse && (
                    <View style={styles.exerciseTypesContainer}>
                      <TouchableOpacity
                        style={styles.exerciseTypesButton}
                        onPress={() => setShowExerciseTypesDropdown(!showExerciseTypesDropdown)}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownTrigger: 'true' } }
                          : {})}
                      >
                        <Text style={styles.exerciseTypesButtonText}>
                          {selectedExerciseType
                            ? selectedExerciseType.exercise_type_name
                            : 'Select Exercise Type'}
                        </Text>
                        <Text style={styles.dropdownArrow}>
                          {showExerciseTypesDropdown ? '▲' : '▼'}
                        </Text>
                      </TouchableOpacity>

                      {showExerciseTypesDropdown && (
                        <View
                          style={styles.exerciseTypesDropdown}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { dropdownSurface: 'true' } }
                            : {})}
                        >
                          {loadingExerciseTypes ? (
                            <Text style={styles.loadingText}>Loading exercise types...</Text>
                          ) : exerciseTypes.length === 0 ? (
                            <Text style={styles.emptyText}>
                              No exercise types found for this course
                            </Text>
                          ) : (
                            exerciseTypes.map((exerciseType) => (
                              <TouchableOpacity
                                key={exerciseType.id}
                                style={styles.exerciseTypesDropdownItem}
                                onPress={() => handleExerciseTypeSelect(exerciseType)}
                                {...(Platform.OS === 'web'
                                  ? { dataSet: { dropdownRow: 'true' } }
                                  : {})}
                              >
                                <Text style={styles.exerciseTypesDropdownItemText}>
                                  {exerciseType.exercise_type_name}
                                </Text>
                              </TouchableOpacity>
                            ))
                          )}
                        </View>
                      )}
                    </View>
                  )}

                  {renderExerciseAssignmentList()}
                </View>
              )}

              {activeAssignmentTab === 'grade' && (
                <View style={styles.nestedTabContent}>
                  <View style={styles.formField}>
                    <Text style={styles.label}>Select Grade</Text>
                    <Text style={styles.helpText}>
                      All grades are listed. You can assign exercises up to one grade above your
                      own (super users see all unless Demo Mode is on).
                    </Text>
                    <TouchableOpacity
                      style={styles.exerciseTypesButton}
                      onPress={() => setShowGradesDropdown(!showGradesDropdown)}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { dropdownTrigger: 'true' } }
                        : {})}
                    >
                      <Text style={styles.exerciseTypesButtonText}>
                        {selectedGrade ? selectedGrade.grade_name : 'Select Grade'}
                      </Text>
                      <Text style={styles.dropdownArrow}>
                        {showGradesDropdown ? '▲' : '▼'}
                      </Text>
                    </TouchableOpacity>

                    {showGradesDropdown && (
                      <View
                        style={styles.exerciseTypesDropdown}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownSurface: 'true' } }
                          : {})}
                      >
                        {loadingGrades ? (
                          <Text style={styles.loadingText}>Loading grades...</Text>
                        ) : grades.length === 0 ? (
                          <Text style={styles.emptyText}>No grades found</Text>
                        ) : (
                          grades.map((grade) => (
                            <TouchableOpacity
                              key={grade.id}
                              style={styles.exerciseTypesDropdownItem}
                              onPress={() => handleGradeSelect(grade)}
                              {...(Platform.OS === 'web'
                                ? { dataSet: { dropdownRow: 'true' } }
                                : {})}
                            >
                              <Text style={styles.exerciseTypesDropdownItemText}>
                                {grade.grade_name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </View>
                    )}
                  </View>

                  {selectedGrade && isGradeBeyondViewerAccess(selectedGrade) && (
                    <Text style={styles.emptyText}>
                      Exercises for this grade are not available at your rank (you may view up to
                      one grade above your own).
                    </Text>
                  )}

                  {selectedGrade &&
                    selectedStudent &&
                    gradeExercises.length > 0 && (
                    <View style={styles.selectAllCourseContainer}>
                      <View style={styles.assignmentTableSelectCol}>
                        <TouchableOpacity
                          style={styles.assignmentTableHeaderCheckbox}
                          onPress={handleSelectAllGradeToggle}
                          disabled={loadingGradeExercises}
                        >
                          <View
                            style={[
                              styles.exerciseCheckbox,
                              selectAllGradeExercises && styles.exerciseCheckboxSelected,
                            ]}
                          >
                            {selectAllGradeExercises && (
                              <Text style={styles.checkmark}>✓</Text>
                            )}
                          </View>
                        </TouchableOpacity>
                      </View>
                      <Text
                        style={[styles.selectAllCourseLabel, styles.selectAllCourseLabelFlex]}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { assignmentsSelectAllLabel: 'true' } }
                          : {})}
                      >
                        Assign all exercises in this grade for this student
                      </Text>
                      <View style={styles.assignmentTableAlreadyAssignedCol} />
                    </View>
                  )}

                  {selectedGrade && (
                    <View style={styles.exerciseTypesContainer}>
                      <TouchableOpacity
                        style={styles.exerciseTypesButton}
                        onPress={() =>
                          setShowExerciseTypesDropdown(!showExerciseTypesDropdown)
                        }
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownTrigger: 'true' } }
                          : {})}
                      >
                        <Text style={styles.exerciseTypesButtonText}>
                          {selectedExerciseType
                            ? selectedExerciseType.exercise_type_name
                            : 'Select Exercise Type'}
                        </Text>
                        <Text style={styles.dropdownArrow}>
                          {showExerciseTypesDropdown ? '▲' : '▼'}
                        </Text>
                      </TouchableOpacity>

                      {showExerciseTypesDropdown && (
                        <View
                          style={styles.exerciseTypesDropdown}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { dropdownSurface: 'true' } }
                            : {})}
                        >
                          {loadingExerciseTypes ? (
                            <Text style={styles.loadingText}>Loading exercise types...</Text>
                          ) : exerciseTypes.length === 0 ? (
                            <Text style={styles.emptyText}>
                              No exercise types found for this grade
                            </Text>
                          ) : (
                            exerciseTypes.map((exerciseType) => (
                              <TouchableOpacity
                                key={exerciseType.id}
                                style={styles.exerciseTypesDropdownItem}
                                onPress={() => handleExerciseTypeSelect(exerciseType)}
                                {...(Platform.OS === 'web'
                                  ? { dataSet: { dropdownRow: 'true' } }
                                  : {})}
                              >
                                <Text style={styles.exerciseTypesDropdownItemText}>
                                  {exerciseType.exercise_type_name}
                                </Text>
                              </TouchableOpacity>
                            ))
                          )}
                        </View>
                      )}
                    </View>
                  )}

                  {loadingExercises && selectedExerciseType ? (
                    <Text style={styles.loadingText}>Loading exercises...</Text>
                  ) : null}

                  {renderExerciseAssignmentList()}
                </View>
              )}

              {activeAssignmentTab === 'exercise-only' && (
                <View style={styles.nestedTabContent}>
                  <View style={styles.formField}>
                    <Text style={styles.label}>Exercise Only</Text>
                    <Text style={styles.helpText}>
                      Standalone exercises not linked to a course or grade. Select a student
                      above, then choose an exercise type below.
                    </Text>
                  </View>

                  {selectedStudent &&
                    exerciseOnlyExercises.length > 0 && (
                    <View style={styles.selectAllCourseContainer}>
                      <View style={styles.assignmentTableSelectCol}>
                        <TouchableOpacity
                          style={styles.assignmentTableHeaderCheckbox}
                          onPress={handleSelectAllExerciseOnlyToggle}
                          disabled={loadingExerciseOnlyExercises}
                        >
                          <View
                            style={[
                              styles.exerciseCheckbox,
                              selectAllExerciseOnlyExercises && styles.exerciseCheckboxSelected,
                            ]}
                          >
                            {selectAllExerciseOnlyExercises && (
                              <Text style={styles.checkmark}>✓</Text>
                            )}
                          </View>
                        </TouchableOpacity>
                      </View>
                      <Text
                        style={[styles.selectAllCourseLabel, styles.selectAllCourseLabelFlex]}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { assignmentsSelectAllLabel: 'true' } }
                          : {})}
                      >
                        Assign all exercise-only exercises for this student
                      </Text>
                      <View style={styles.assignmentTableAlreadyAssignedCol} />
                    </View>
                  )}

                  <View style={styles.exerciseTypesContainer}>
                    <TouchableOpacity
                      style={styles.exerciseTypesButton}
                      onPress={() =>
                        setShowExerciseTypesDropdown(!showExerciseTypesDropdown)
                      }
                      {...(Platform.OS === 'web'
                        ? { dataSet: { dropdownTrigger: 'true' } }
                        : {})}
                    >
                      <Text style={styles.exerciseTypesButtonText}>
                        {selectedExerciseType
                          ? selectedExerciseType.exercise_type_name
                          : 'Select Exercise Type'}
                      </Text>
                      <Text style={styles.dropdownArrow}>
                        {showExerciseTypesDropdown ? '▲' : '▼'}
                      </Text>
                    </TouchableOpacity>

                    {showExerciseTypesDropdown && (
                      <View
                        style={styles.exerciseTypesDropdown}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { dropdownSurface: 'true' } }
                          : {})}
                      >
                        {loadingExerciseTypes ? (
                          <Text style={styles.loadingText}>Loading exercise types...</Text>
                        ) : exerciseTypes.length === 0 ? (
                          <Text style={styles.emptyText}>
                            No exercise-only types available
                          </Text>
                        ) : (
                          exerciseTypes.map((exerciseType) => (
                            <TouchableOpacity
                              key={exerciseType.id}
                              style={styles.exerciseTypesDropdownItem}
                              onPress={() => handleExerciseTypeSelect(exerciseType)}
                              {...(Platform.OS === 'web'
                                ? { dataSet: { dropdownRow: 'true' } }
                                : {})}
                            >
                              <Text style={styles.exerciseTypesDropdownItemText}>
                                {exerciseType.exercise_type_name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </View>
                    )}
                  </View>

                  {loadingExercises && selectedExerciseType ? (
                    <Text style={styles.loadingText}>Loading exercises...</Text>
                  ) : null}

                  {renderExerciseAssignmentList()}
                </View>
              )}
              </View>
            </View>
          </View>
        )}
        {activeTab === 'my-assigned' && (
          <View style={styles.tabContent}>
            <Text style={styles.tabContentText}>My Assigned Exercises content will go here</Text>
          </View>
        )}
      </ScrollView>

      {/* Exercise Detail Modal */}
      <Modal
        visible={showExerciseDetailModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowExerciseDetailModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowExerciseDetailModal(false)}
        >
          <View style={styles.modalContent} onStartShouldSetResponder={() => true}>
            {selectedExerciseForDetail && (
              <>
                {/* Close Button */}
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={() => setShowExerciseDetailModal(false)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.modalCloseButtonText}>✕</Text>
                </TouchableOpacity>

                {/* Exercise Name */}
                <Text style={styles.modalExerciseName}>
                  {selectedExerciseForDetail.exercise_name}
                </Text>

                {/* Image or Video */}
                <View style={styles.modalMediaContainer}>
                  {isYouTubeUrl(selectedExerciseForDetail.display_url) ? (
                    Platform.OS === 'web' ? (
                      <View style={styles.youtubeContainer}>
                        <iframe
                          src={getYouTubeEmbedUrl(selectedExerciseForDetail.display_url)}
                          style={styles.youtubeIframe}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </View>
                    ) : (
                      <View style={styles.youtubeContainer}>
                        <Text style={styles.youtubeLinkText}>
                          YouTube Video:{'\n'}
                          {selectedExerciseForDetail.display_url}
                        </Text>
                        <Text style={styles.youtubeNoteText}>
                          (Video playback available on web version)
                        </Text>
                      </View>
                    )
                  ) : (
                    <ExpoImage
                      source={
                        selectedExerciseForDetail.display_url
                          ? { uri: selectedExerciseForDetail.display_url }
                          : PLACEHOLDER_IMAGE
                      }
                      style={styles.modalImage}
                      contentFit="contain"
                      onError={() => {
                        console.log('Image failed to load, using placeholder');
                      }}
                    />
                  )}
                </View>

                {/* Description */}
                {selectedExerciseForDetail.description && (
                  <View style={styles.modalDescriptionContainer}>
                    <Text style={styles.modalDescriptionLabel}>Description:</Text>
                    <Text style={styles.modalDescriptionText}>
                      {selectedExerciseForDetail.description}
                    </Text>
                  </View>
                )}

                {/* Exercise Details */}
                <View style={styles.modalExerciseDetails}>
                  <Text style={styles.modalExerciseDetailsText}>
                    {selectedExerciseForDetail.sets} sets × {selectedExerciseForDetail.reps} reps
                  </Text>
                </View>
              </>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    borderBottomWidth: 0,
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumbContainer: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    letterSpacing: 0.5,
    backgroundColor: 'transparent',
  },
  profileIconContainer: {
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  tabsWrapper: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: theme.isDarkMode ? 15 : 10,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  tabsContainer: {
    width: '90%',
    flexDirection: 'row',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: theme.isDarkMode ? 8 : 8,
    padding: theme.isDarkMode ? 4 : 0,
    borderWidth: theme.isDarkMode ? 0 : 1,
    borderColor: theme.isDarkMode ? 'transparent' : '#E0E0E0',
    borderBottomWidth: theme.isDarkMode ? 0 : 2,
    borderBottomColor: theme.isDarkMode ? 'transparent' : '#E0E0E0',
    ...(theme.isDarkMode && Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    } : theme.isDarkMode ? {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 4,
    } : {}),
  },
  tab: {
    flex: 1,
    paddingVertical: theme.isDarkMode ? 12 : 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    borderRadius: theme.isDarkMode ? 6 : 0,
    backgroundColor: theme.isDarkMode ? theme.colors.background : 'transparent',
    marginHorizontal: theme.isDarkMode ? 2 : 0,
  },
  tabActive: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : 'transparent',
    ...(theme.isDarkMode && Platform.OS === 'web' ? {
      boxShadow: '0 2px 4px rgba(253, 253, 10, 0.2)',
    } : theme.isDarkMode ? {
      shadowColor: '#FDFD0A',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 3,
    } : {}),
  },
  tabText: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.isDarkMode ? theme.colors.text : '#666666',
    backgroundColor: 'transparent',
  },
  tabTextActive: {
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    backgroundColor: 'transparent',
  },
  tabIndicator: {
    position: 'absolute',
    bottom: theme.isDarkMode ? -4 : -2,
    left: theme.isDarkMode ? '10%' : 0,
    right: theme.isDarkMode ? '10%' : 0,
    height: theme.isDarkMode ? 3 : 3,
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    borderRadius: theme.isDarkMode ? 2 : 0,
    ...(theme.isDarkMode ? {
      width: '80%',
      alignSelf: 'center',
    } : {}),
  },
  content: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  contentContainer: {
    width: '90%',
    alignSelf: 'center',
    padding: 20,
    paddingBottom: 40,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: theme.isDarkMode ? 8 : 0,
    marginTop: theme.isDarkMode ? 10 : 0,
  },
  tabContent: {
    flex: 1,
    minHeight: 200,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  tabContentText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    textAlign: 'center',
    marginTop: 20,
    backgroundColor: 'transparent',
  },
  createExerciseLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
    backgroundColor: theme.isDarkMode ? 'transparent' : 'transparent',
  },
  createExerciseText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    backgroundColor: 'transparent',
  },
  createExerciseButton: {
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  createExerciseButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? '#000000' : '#FFFFFF',
    backgroundColor: 'transparent',
  },
  formField: {
    marginBottom: 20,
    zIndex: 2,
    backgroundColor: 'transparent',
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  helpText: {
    fontSize: 13,
    color: theme.colors.text,
    opacity: 0.75,
    marginBottom: 8,
    fontStyle: 'italic',
    backgroundColor: 'transparent',
  },
  dropdownTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.isDarkMode ? theme.colors.inputBackground : '#F5F5F5',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    minHeight: 48,
  },
  dropdownTriggerText: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
    marginRight: 8,
    backgroundColor: 'transparent',
  },
  dropdownList: {
    marginTop: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    maxHeight: 220,
  },
  dropdownListItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
  },
  dropdownListItemText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    padding: 15,
    minHeight: 50,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownButtonTextPlaceholder: {
    color: theme.isDarkMode ? '#888' : '#999',
    backgroundColor: 'transparent',
  },
  dropdownArrow: {
    fontSize: 12,
    color: theme.isDarkMode ? theme.colors.text : '#666',
    marginLeft: 10,
    backgroundColor: 'transparent',
  },
  dropdownOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 1000,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    maxHeight: 300,
    zIndex: 1001,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  dropdownScrollView: {
    maxHeight: 300,
  },
  dropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
    backgroundColor: 'transparent',
  },
  dropdownOptionSelected: {
    backgroundColor: theme.isDarkMode ? 'rgba(253, 253, 10, 0.1)' : 'rgba(212, 175, 55, 0.1)',
  },
  checkboxContainer: {
    marginRight: 12,
  },
  // Checkbox styles for student dropdown (theme-aware)
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  checkboxSelected: {
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
  },
  checkmark: {
    color: theme.isDarkMode ? '#000000' : '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
    backgroundColor: 'transparent',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownOptionTextSelected: {
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    backgroundColor: 'transparent',
  },
  loadingText: {
    padding: 15,
    textAlign: 'center',
    color: theme.isDarkMode ? theme.colors.text : '#666',
    fontSize: 14,
    backgroundColor: 'transparent',
  },
  emptyText: {
    padding: 15,
    textAlign: 'center',
    color: theme.isDarkMode ? theme.colors.text : '#666',
    fontSize: 14,
    fontStyle: 'italic',
    backgroundColor: 'transparent',
  },
  // Nested Tabs Styles
  nestedTabsWrapper: {
    width: '100%',
    alignItems: 'center',
    marginTop: 20,
    paddingVertical: theme.isDarkMode ? 10 : 8,
  },
  nestedTabsCard: {
    width: '90%',
    alignSelf: 'center',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: theme.isDarkMode ? 0 : 1,
    borderColor: theme.isDarkMode ? 'transparent' : '#E0E0E0',
    overflow: 'visible',
    ...(theme.isDarkMode && Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    } : theme.isDarkMode ? {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
      elevation: 4,
    } : {}),
  },
  nestedTabsContainer: {
    flexDirection: 'row',
    padding: theme.isDarkMode ? 3 : 0,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    backgroundColor: 'transparent',
  },
  nestedTab: {
    flex: 1,
    paddingVertical: theme.isDarkMode ? 10 : 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    borderRadius: theme.isDarkMode ? 5 : 0,
    backgroundColor: theme.isDarkMode ? theme.colors.background : 'transparent',
    marginHorizontal: theme.isDarkMode ? 1.5 : 0,
  },
  nestedTabActive: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : 'transparent',
  },
  nestedTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.isDarkMode ? theme.colors.text : '#666666',
    backgroundColor: 'transparent',
  },
  nestedTabTextActive: {
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    backgroundColor: 'transparent',
  },
  nestedTabIndicator: {
    position: 'absolute',
    bottom: theme.isDarkMode ? -3 : -2,
    left: theme.isDarkMode ? '15%' : 0,
    right: theme.isDarkMode ? '15%' : 0,
    height: 2,
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    borderRadius: theme.isDarkMode ? 1 : 0,
    ...(theme.isDarkMode ? {
      width: '70%',
      alignSelf: 'center',
    } : {}),
  },
  nestedTabContent: {
    padding: 15,
    backgroundColor: 'transparent',
    overflow: 'visible',
  },
  // Course Tile Styles
  courseTileContainer: {
    marginBottom: 20,
  },
  courseTile: {
    width: '100%',
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#F5F5F5',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
  },
  courseTileImage: {
    width: '100%',
    height: 120,
    marginBottom: 12,
    borderRadius: 6,
  },
  courseTileButton: {
    width: '100%',
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    padding: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  courseTileButtonText: {
    color: theme.isDarkMode ? '#000000' : '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  coursesDropdown: {
    marginTop: 10,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    overflow: 'hidden',
  },
  coursesDropdownItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
    backgroundColor: 'transparent',
  },
  coursesDropdownItemText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    backgroundColor: 'transparent',
  },
  selectAllCourseContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingHorizontal: 0,
  },
  selectAllCourseLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    backgroundColor: 'transparent',
    flex: 1,
    minWidth: 0,
    paddingRight: 8,
  },
  selectAllCourseLabelFlex: {
    flex: 1,
    minWidth: 0,
  },
  exerciseRowWrapper: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginBottom: 10,
    width: '100%',
  },
  // Exercise Types Styles
  exerciseTypesContainer: {
    marginBottom: 20,
  },
  exerciseTypesButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    padding: 15,
    minHeight: 50,
  },
  exerciseTypesButtonText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    flex: 1,
    backgroundColor: 'transparent',
  },
  exerciseTypesDropdown: {
    marginTop: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  exerciseTypesDropdownItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
    backgroundColor: 'transparent',
  },
  exerciseTypesDropdownItemText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    backgroundColor: 'transparent',
  },
  // Exercises list — fixed columns so text does not collapse on web
  exercisesDropdown: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  assignmentTableHeader: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingBottom: 10,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  assignmentTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    flex: 1,
  },
  assignmentTableSelectCol: {
    width: 44,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: 6,
  },
  assignmentTableExerciseCol: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    flexShrink: 1,
  },
  assignmentTableAlreadyAssignedCol: {
    width: 56,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  assignmentTableAlreadyAssignedHeader: {
    alignItems: 'center',
    paddingBottom: 4,
  },
  assignmentTableHeaderLabel: {
    fontSize: 11,
    color: theme.colors.text,
    opacity: 0.85,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 4,
    backgroundColor: 'transparent',
  },
  assignmentTableHeaderLabelGreen: {
    fontSize: 11,
    color: theme.colors.text,
    opacity: 0.9,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 14,
    backgroundColor: 'transparent',
  },
  assignmentTableGreenUnderline: {
    marginTop: 4,
    height: 2,
    width: '100%',
    minWidth: 48,
    backgroundColor: '#4CAF50',
    borderRadius: 1,
  },
  assignmentTableHeaderCheckbox: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  checkboxContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Exercise checkbox styles (matching SessionBuilder - white border, yellow when selected)
  exerciseCheckbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  exerciseCheckboxSelected: {
    backgroundColor: '#d4af37',
    borderColor: '#d4af37',
  },
  exerciseTile: {
    flex: 1,
    minWidth: 0,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    position: 'relative',
    overflow: 'hidden',
  },
  exerciseTileSelected: {
    backgroundColor: theme.isDarkMode ? '#000000' : '#000000',
  },
  exerciseInfo: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  exerciseName: {
    color: theme.colors.text,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
    backgroundColor: 'transparent',
  },
  exerciseDetails: {
    color: theme.colors.text,
    fontSize: 13,
    opacity: 0.7,
    backgroundColor: 'transparent',
  },
  checkboxGreen: {
    borderColor: '#4CAF50',
  },
  checkboxSelectedGreen: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  yellowOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#d4af37',
    opacity: 0.5,
    borderRadius: 8,
  },
  exerciseNameSelected: {
    color: '#FFFFFF',
  },
  exerciseDetailsSelected: {
    color: '#FFFFFF',
    opacity: 0.9,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 10,
  },
  actionButton: {
    padding: 12,
    borderRadius: 8,
    marginHorizontal: 4,
    minWidth: 100,
    alignItems: 'center',
  },
  assignButton: {
    backgroundColor: '#d4af37',
  },
  assignButtonDisabled: {
    opacity: 0.5,
  },
  retireButton: {
    backgroundColor: theme.isDarkMode ? '#555555' : '#9E9E9E',
  },
  retireButtonDisabled: {
    opacity: 0.45,
  },
  retireButtonTextDisabled: {
    color: theme.isDarkMode ? '#CCCCCC' : '#EEEEEE',
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: width * 0.9,
    maxHeight: '80%',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    position: 'relative',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    } : {
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.5,
      shadowRadius: 10,
      elevation: 10,
    }),
  },
  modalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  modalCloseButtonText: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalExerciseName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.heading || '#d4af37',
    marginBottom: 15,
    textAlign: 'center',
  },
  modalMediaContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 15,
  },
  modalImage: {
    width: '100%',
    height: 250,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? '#000' : '#F5F5F5',
  },
  youtubeContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  youtubeIframe: {
    width: '100%',
    height: '100%',
  },
  youtubeLinkText: {
    color: theme.colors.text,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 10,
  },
  youtubeNoteText: {
    color: theme.colors.text,
    fontSize: 12,
    textAlign: 'center',
    fontStyle: 'italic',
    opacity: 0.7,
  },
  modalDescriptionContainer: {
    marginTop: 15,
    marginBottom: 10,
  },
  modalDescriptionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  modalDescriptionText: {
    fontSize: 14,
    color: theme.colors.text,
    lineHeight: 20,
    textAlign: 'left',
  },
  modalExerciseDetails: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  modalExerciseDetailsText: {
    fontSize: 14,
    color: theme.colors.text,
    textAlign: 'center',
  },
  assignmentSuccessBox: {
    backgroundColor: theme.colors.heading || '#FDFD0A',
    padding: 15,
    borderRadius: 8,
    marginTop: 15,
    alignItems: 'center',
  },
  assignmentSuccessText: {
    color: theme.isDarkMode ? '#000000' : '#000000',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});
